// Utilities
import {gsap} from 'gsap';
import WindowMetrics from '../../utilities/window-metrics';

const settings = {
    classNames: {
        open: 'menu-item--open',
        closing: 'menu-item--closing',
    },

    selectors: {
        button: ':scope > button',
        menu: '.sub-menu',
        menuList: '.sub-menu > ul',
    },

    cssProperties: {
        shift: '--sub-menu-shift',
    },
};

export default class SubMenu {
    constructor(
        container,
        finalMobileBreakpoint = WindowMetrics.breakpoints.small,
        callbacks = {},
    ) {
        this.container = container;
        this.finalMobileBreakpoint = finalMobileBreakpoint;
        this.callbacks = callbacks;
        this.isOpen = false;
        this.isShifted = false;

        this.button = this.container
            .querySelector(settings.selectors.button);
        this.menu = this.container
            .querySelector(settings.selectors.menu);
        this.menuList = this.container
            .querySelector(settings.selectors.menuList);

        if (
            !this.button ||
            !this.menu ||
            !this.menuList
        ) {
            throw new Error();
        }

        this.container.addEventListener('mouseenter', (event) => {
            // Desktop only
            if (
                WindowMetrics.isMobile(
                    WindowMetrics.currentBreakpoint,
                    this.finalMobileBreakpoint,
                )
            ) {
                return;
            }

            this.open();
        });

        this.container.addEventListener('mouseleave', (event) => {
            // Desktop only
            if (
                WindowMetrics.isMobile(
                    WindowMetrics.currentBreakpoint,
                    this.finalMobileBreakpoint,
                )
            ) {
                return;
            }

            this.close();
        });

        let lastClickTime = 0;
        this.button.addEventListener('click', (event) => {
            // Mobile only
            if (
                !WindowMetrics.isMobile(
                    WindowMetrics.currentBreakpoint,
                    this.finalMobileBreakpoint,
                )
            ) {
                return;
            }

            // Debounce double-click events
            const newClickTime = (new Date()).getTime();
            if (newClickTime - lastClickTime < 100) {
                return;
            }

            lastClickTime = newClickTime;

            if (this.isOpen) {
                this.close();
            } else {
                this.open();
            }
        });

        this.button.addEventListener('touchstart', (event) => {
            // Desktop only
            if (
                WindowMetrics.isMobile(
                    WindowMetrics.currentBreakpoint,
                    this.finalMobileBreakpoint,
                )
            ) {
                return;
            }

            if (this.isOpen) {
                this.close();
            } else {
                this.open();
            }
        });

        this.button.addEventListener('keydown', (event) => {
            if (event.key != 'Enter') {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (this.isOpen) {
                this.close();
            } else {
                this.open();
            }
        });
    }

    open(immediate = false) {
        if (this.isOpen) {
            return;
        }

        this.isOpen = true;

        // Stop any ongoing animation
        if (this.timeline) {
            this.timeline.progress(1);
        }

        this.container.classList.add(settings.classNames.open);

        // On desktop, check if we're clipping offscreen to the right
        let shiftAmount;
        if (
            !WindowMetrics.isMobile(
                WindowMetrics.currentBreakpoint,
                this.finalMobileBreakpoint,
            )
        ) {
            const bounds = this.menu.getBoundingClientRect();
            const viewportEdge = window.innerWidth - 16;
            const overflow = bounds.right - viewportEdge;

            if (overflow > 0) {
                shiftAmount = Math.round(overflow * 10) / 10;
            }
        }

        if (shiftAmount) {
            this.isShifted = true;
            this.container.style.setProperty(
                settings.cssProperties.shift,
                `-${shiftAmount}px`,
            );
        } else if (this.isShifted) {
            this.isShifted = false;
            this.container.removeAttribute('style');
        }

        // Perform start callback
        if (this.callbacks.onOpenStart) {
            this.callbacks.onOpenStart(this);
        }

        if (immediate) {
            // Perform end callback
            if (this.callbacks.onOpenEnd) {
                this.callbacks.onOpenEnd(this);
            }
        }

        // Create timeline
        this.timeline = gsap.timeline();

        this.timeline.from(
            this.menuList,
            {
                duration: 0.5,
                ease: 'power3.out',
                height: 0,
            },
            0,
        );

        this.timeline.add(() => {
            // Remove explicit height from the menu
            this.menuList.removeAttribute('style');

            // Perform end callback
            if (this.callbacks.onOpenEnd) {
                this.callbacks.onOpenEnd(this);
            }

            // Clear our timeline variable
            this.timeline = null;
        });
    }

    close(immediate = false) {
        if (!this.isOpen) {
            return;
        }

        this.isOpen = false;

        // Stop any ongoing animation
        if (this.timeline) {
            this.timeline.progress(1);
        }

        // Perform start callback
        if (this.callbacks.onCloseStart) {
            this.callbacks.onCloseStart(this);
        }

        this.container.classList.add(settings.classNames.closing);
        this.container.classList.remove(settings.classNames.open);

        if (immediate) {
            this.container.classList.remove(settings.classNames.closing);

            if (this.isShifted) {
                this.isShifted = false;
                this.container.removeAttribute('style');
            }

            // Perform end callback
            if (this.callbacks.onCloseEnd) {
                this.callbacks.onCloseEnd(this);
            }
        }

        // Create timeline
        this.timeline = gsap.timeline();

        let duration;
        let ease;
        if (
            WindowMetrics.isMobile(
                WindowMetrics.currentBreakpoint,
                this.finalMobileBreakpoint,
            )
        ) {
            duration = 0.5;
            ease = 'power3.out';
        } else {
            duration = 0.25;
            ease = 'power2.out';
        }

        this.timeline.to(
            this.menuList,
            {
                duration: duration,
                ease: ease,
                height: 0,
            },
            0,
        );

        this.timeline.add(() => {
            this.container.classList.remove(settings.classNames.closing);

            if (this.isShifted) {
                this.isShifted = false;
                this.container.removeAttribute('style');
            }

            // Remove explicit height from the menu
            this.menuList.removeAttribute('style');

            // Perform end callback
            if (this.callbacks.onCloseEnd) {
                this.callbacks.onCloseEnd(this);
            }

            // Clear our timeline variable
            this.timeline = null;
        });
    }
}
